import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Paperclip, Search, X, FileText } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getDateInputValue } from '../utils/helpers';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LossReasonDialog from '../components/LossReasonDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { usePagedTable, SortableTh, PaginationBar } from '../utils/tableUtils';

// Product-type → category classifier used for the category badge/filter.
const LIFE = ['Term', 'Endowment', 'ULIP', 'Whole Life'];
const GENERAL = ['Health', 'Motor', 'Home', 'Travel', 'Personal Accident', 'Commercial'];
function categoryOf(pt) {
  if (LIFE.includes(pt))    return 'insurance:life';
  if (GENERAL.includes(pt)) return 'insurance:general';
  return 'insurance:other';
}
const CAT_LABEL = {
  'insurance:life':      { label: 'Life',      color: 'bg-blue-100 text-blue-800' },
  'insurance:general':   { label: 'General',   color: 'bg-emerald-100 text-emerald-800' },
  'insurance:other':     { label: 'Other',     color: 'bg-slate-100 text-slate-800' },
  'investment':          { label: 'Investment',color: 'bg-purple-100 text-purple-800' },
  'loans':               { label: 'Loans',     color: 'bg-amber-100 text-amber-800' },
};

const Policies = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCategory = searchParams.get('category') || 'all';

  // Round 59 · Item 1 — client typeahead state must be declared before the
  // filteredPolicies useMemo which depends on it (otherwise TDZ ReferenceError).
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  // Apply URL category filter AND the client typeahead picker before paging.
  const filteredPolicies = useMemo(() => {
    let rows = policies;
    if (selectedClient) rows = rows.filter(p => p.client_id === selectedClient.client_id);
    if (currentCategory !== 'all') {
      if (currentCategory === 'investment' || currentCategory === 'loans') return [];
      rows = rows.filter(p => categoryOf(p.product_type) === currentCategory);
    }
    // Round 63 · Fix B — synthesise a `status_display` field so the status
    // filter dropdown can offer a "Lost" option that matches on renewal_status
    // without the engine learning new tricks. "Lost" shows for Lapsed policies
    // whose renewal was explicitly marked lost by the agent.
    return rows.map(p => ({
      ...p,
      status_display: p.renewal_status === 'lost' ? 'Lost' : p.status,
    }));
  }, [policies, currentCategory, selectedClient]);

  const policiesTable = usePagedTable({ rows: filteredPolicies, defaultPageSize: 25, defaultSort: { key: 'policy_number', dir: 'asc' } });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [dueManuallySet, setDueManuallySet] = useState(false);  // Round 59 · Item 2
  const [deletingPolicy, setDeletingPolicy] = useState(null);   // Round 59 · Item 4 — AlertDialog target
  const [policyDocFile, setPolicyDocFile] = useState(null);
  // Round 44 · Item 1+4: intercept any status→Lost transition (edit form or
  // one-tap badge) with a reason picker so the loss-analysis charts keep
  // clustering cleanly.
  const [lostReasonState, setLostReasonState] = useState({ open: false, source: '', policy: null });
  const [lostReasonSaving, setLostReasonSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '', policy_number: '', insurer: '', product_name: '', product_type: '',
    plan_type: '', sum_assured: '', premium_amount: '', premium_frequency: 'Yearly',
    net_premium: '', gst_percent: '', gst_amount: '', total_premium: '', total_premium_touched: false,
    start_date: '', end_date: '', next_due_date: '', nominee: '', status: 'Active', policy_stage: ''
  });

  const addDaysISO = (isoDate, days) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const DEFAULT_TERM_DAYS = 364;

  // Only auto-fill end_date if empty or still matches the previous auto value
  const onStartDateChange = (nextStart) => {
    setFormData((f) => {
      const prevAuto = f.start_date ? addDaysISO(f.start_date, DEFAULT_TERM_DAYS) : '';
      const shouldAutoFill = !f.end_date || f.end_date === prevAuto;
      return {
        ...f,
        start_date: nextStart,
        end_date: shouldAutoFill && nextStart ? addDaysISO(nextStart, DEFAULT_TERM_DAYS) : f.end_date,
      };
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  // Round 43 · Item 1: honour ?edit={policy_id} so links from PolicyDetail
  // (and any deep-link from an email/reminder) open the edit dialog in
  // place instead of dumping the agent on the list view. Removes the query
  // param after opening so a back-nav doesn't re-open the dialog.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || policies.length === 0) return;
    const match = policies.find(p => p.policy_id === editId);
    if (match) {
      openEditDialog(match);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies, searchParams]);

  const loadData = async () => {
    try {
      const [policiesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/policies`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true })
      ]);
      setPolicies(policiesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Round 44 · Item 1: if editing to status=Lost, capture the reason first.
    // We stash the pending submit until the picker returns; the actual PUT
    // fires from within submitPolicyLostReason().
    if (editingPolicy && formData.status === 'Lost' && editingPolicy.status !== 'Lost') {
      setLostReasonState({ open: true, source: 'edit-form', policy: { ...editingPolicy } });
      return;
    }
    await persistPolicy();
  };

  const persistPolicy = async (extraFields = {}) => {
    try {
      let policyId;
      const payload = { ...formData, ...extraFields };
      if (editingPolicy) {
        await axios.put(`${API}/policies/${editingPolicy.policy_id}`, payload, { withCredentials: true });
        policyId = editingPolicy.policy_id;
        toast.success('Policy updated successfully');
      } else {
        const { data } = await axios.post(`${API}/policies`, payload, { withCredentials: true });
        policyId = data.policy_id;
        toast.success('Policy created successfully');
      }
      // Best-effort document upload — same helper both Add and Edit use.
      if (policyDocFile && policyId) {
        try {
          const fd = new FormData();
          fd.append('file', policyDocFile);
          await axios.post(`${API}/policies/${policyId}/documents?doc_type=policy`, fd, {
            withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success(`Document "${policyDocFile.name}" attached`);
        } catch (docErr) {
          toast.warning(docErr.response?.data?.detail || 'Policy saved, but document upload failed.');
        }
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  // Round 44 · Item 4: one-tap Active → Lapsed → Lost cycle on the status
  // badge. Lost also fires the reason picker.
  const cycleStatus = async (policy) => {
    const order = ['Active', 'Lapsed', 'Lost'];
    const idx = order.indexOf(policy.status);
    const next = order[(idx + 1) % order.length];
    if (next === 'Lost') {
      setLostReasonState({ open: true, source: 'quick-badge', policy });
      return;
    }
    try {
      await axios.put(`${API}/policies/${policy.policy_id}`, { status: next }, { withCredentials: true });
      toast.success(`Status → ${next}`);
      loadData();
    } catch (e) {
      toast.error('Could not change status');
    }
  };

  const submitPolicyLostReason = async (reason, notes) => {
    const { source, policy } = lostReasonState;
    if (!policy) return;
    setLostReasonSaving(true);
    try {
      if (source === 'edit-form') {
        // Fires the same submit flow that was intercepted, with reason
        // fields merged in so the audit trail keeps them together.
        await persistPolicy({ loss_reason: reason, loss_notes: notes });
      } else {
        await axios.put(`${API}/policies/${policy.policy_id}`, {
          status: 'Lost', loss_reason: reason, loss_notes: notes,
        }, { withCredentials: true });
        toast.success('Marked Lost — moved to Lost Leads');
        loadData();
      }
      setLostReasonState({ open: false, source: '', policy: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark Lost');
    } finally {
      setLostReasonSaving(false);
    }
  };

  const handleDelete = async (policyId) => {
    // Opens the AlertDialog instead of using window.confirm (Round 59 · Item 4).
    const p = policies.find(x => x.policy_id === policyId);
    setDeletingPolicy(p || { policy_id: policyId });
  };

  const confirmDelete = async () => {
    if (!deletingPolicy) return;
    try {
      const { data } = await axios.delete(`${API}/policies/${deletingPolicy.policy_id}`, { withCredentials: true });
      const d = data.deleted || {};
      const extras = [];
      if (d.commissions)   extras.push(`${d.commissions} commission${d.commissions === 1 ? '' : 's'}`);
      if (d.reminders)     extras.push(`${d.reminders} reminder log${d.reminders === 1 ? '' : 's'}`);
      if (d.notifications) extras.push(`${d.notifications} notification${d.notifications === 1 ? '' : 's'}`);
      if (d.tasks)         extras.push(`${d.tasks} task${d.tasks === 1 ? '' : 's'}`);
      toast.success(extras.length ? `Policy deleted · cleaned up ${extras.join(', ')}` : 'Policy deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete policy');
    } finally {
      setDeletingPolicy(null);
    }
  };

  const openEditDialog = (policy) => {
    setEditingPolicy(policy);
    setDueManuallySet(false);  // reset guard on every dialog open (Round 59 · Item 2)
    const pt = policy.product_type;
    const isZeroGst = pt === 'Term' || pt === 'Term Life' || pt === 'Health';
    setFormData({
      client_id: policy.client_id,
      policy_number: policy.policy_number,
      insurer: policy.insurer,
      product_name: policy.product_name,
      product_type: policy.product_type,
      plan_type: policy.plan_type || '',
      sum_assured: policy.sum_assured,
      premium_amount: policy.premium_amount,
      net_premium: policy.net_premium ?? '',
      gst_percent: policy.gst_percent != null ? String(policy.gst_percent) : (isZeroGst ? '0' : '18'),
      gst_amount: policy.gst_amount ?? '',
      total_premium: policy.total_premium ?? '',
      total_premium_touched: !!policy.total_premium,
      premium_frequency: policy.premium_frequency,
      start_date: getDateInputValue(policy.start_date),
      end_date: getDateInputValue(policy.end_date),
      next_due_date: getDateInputValue(policy.next_due_date),
      nominee: policy.nominee || '',
      status: policy.status,
      policy_stage: policy.policy_stage || '',  // Round 61 · Item 4
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPolicy(null);
    setPolicyDocFile(null);
    setFormData({
      client_id: '', policy_number: '', insurer: '', product_name: '', product_type: '',
      plan_type: '', sum_assured: '', premium_amount: '', premium_frequency: 'Yearly',
      net_premium: '', gst_percent: '', gst_amount: '', total_premium: '', total_premium_touched: false,
      start_date: '', end_date: '', next_due_date: '', nominee: '', status: 'Active', policy_stage: ''
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Lapsed': 'bg-red-100 text-red-800',
      'Surrendered': 'bg-orange-100 text-orange-800',
      'Matured': 'bg-blue-100 text-blue-800',
      'Claimed': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client ? client.name : 'Unknown';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Policies</h1>
          <p className="text-slate-600 mt-1">Manage life and general insurance policies</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-policy-button">
              <Plus className="h-4 w-4 mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Add New Policy'}</DialogTitle>
              <DialogDescription>Fill in the policy details below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="client_id">Client *</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})} required>
                    <SelectTrigger data-testid="policy-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.client_id} value={client.client_id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="policy_number">Policy Number *</Label>
                  <Input id="policy_number" value={formData.policy_number} onChange={(e) => setFormData({...formData, policy_number: e.target.value})} required data-testid="policy-number-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insurer">Insurer *</Label>
                  <Input id="insurer" value={formData.insurer} onChange={(e) => setFormData({...formData, insurer: e.target.value})} required data-testid="policy-insurer-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product_type">Product Type *</Label>
                  <Select value={formData.product_type} onValueChange={(value) => setFormData({...formData, product_type: value, gst_percent: (value === 'Term' || value === 'Health') ? '0' : (formData.gst_percent || '18')})}>
                    <SelectTrigger data-testid="policy-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Term">Term</SelectItem>
                      <SelectItem value="Endowment">Endowment</SelectItem>
                      <SelectItem value="ULIP">ULIP</SelectItem>
                      <SelectItem value="Whole Life">Whole Life</SelectItem>
                      <SelectItem value="Health">Health</SelectItem>
                      <SelectItem value="Motor">Motor</SelectItem>
                      <SelectItem value="Travel">Travel</SelectItem>
                      <SelectItem value="Home">Home</SelectItem>
                      <SelectItem value="Personal Accident">Personal Accident</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product_name">Product Name *</Label>
                  <Input id="product_name" value={formData.product_name}
                    onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                    required
                    disabled={!formData.product_type}
                    placeholder={formData.product_type ? '' : 'Select Product Type first'}
                    data-testid="policy-product-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sum_assured">Sum Assured (₹) *</Label>
                  <Input id="sum_assured" type="number" min="0" placeholder="Enter sum assured" value={formData.sum_assured} onChange={(e) => setFormData({...formData, sum_assured: e.target.value})} required data-testid="policy-sum-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="net_premium">Net Premium (₹) *</Label>
                  <Input id="net_premium" type="number" min="0" placeholder="Base premium before GST" value={formData.net_premium || ''}
                    required
                    onChange={(e) => {
                      const net = e.target.value;
                      setFormData((f) => {
                        const netN = parseFloat(net) || 0;
                        const gstPct = parseFloat(f.gst_percent) || 0;
                        const gstAmt = netN * (gstPct / 100);
                        const nextTotal = f.total_premium_touched ? f.total_premium : (net === '' && !gstPct ? '' : (netN + gstAmt).toFixed(2));
                        return { ...f, net_premium: net, gst_amount: gstAmt ? gstAmt.toFixed(2) : '', total_premium: nextTotal };
                      });
                    }}
                    data-testid="policy-net-premium-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_percent">GST % *</Label>
                  <Input id="gst_percent" type="number" min="0" step="0.1" placeholder="18 (or 0 for Term/Health)"
                    value={formData.gst_percent ?? ''}
                    onChange={(e) => {
                      const gstPct = e.target.value;
                      setFormData((f) => {
                        const netN = parseFloat(f.net_premium) || 0;
                        const gstAmt = gstPct === '' ? 0 : netN * (parseFloat(gstPct) / 100);
                        const nextTotal = f.total_premium_touched ? f.total_premium : (f.net_premium === '' && gstPct === '' ? '' : (netN + gstAmt).toFixed(2));
                        return { ...f, gst_percent: gstPct, gst_amount: gstAmt ? gstAmt.toFixed(2) : '', total_premium: nextTotal };
                      });
                    }}
                    data-testid="policy-gst-input" />
                  <p className="text-[10px] text-slate-500">Defaults to 18%. Term Life &amp; Health are typically 0%.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_premium">Total Premium (₹)</Label>
                  <Input id="total_premium" type="number" min="0" placeholder="Auto: Net + GST (editable)" value={formData.total_premium || ''}
                    onChange={(e) => setFormData({...formData, total_premium: e.target.value, total_premium_touched: true})}
                    data-testid="policy-total-premium-input" />
                  <p className="text-[10px] text-slate-500">Auto-computed as Net + GST. Once you edit this, auto-calc stops until you clear the field.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="premium_frequency">Premium Frequency *</Label>
                  <Select value={formData.premium_frequency} onValueChange={(value) => setFormData({...formData, premium_frequency: value})}>
                    <SelectTrigger data-testid="policy-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                      <SelectItem value="Half-Yearly">Half-Yearly</SelectItem>
                      <SelectItem value="Yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input id="start_date" type="date" value={formData.start_date} onChange={(e) => onStartDateChange(e.target.value)} required data-testid="policy-start-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input id="end_date" type="date" value={formData.end_date} onChange={(e) => {
                    const nextEnd = e.target.value;
                    setFormData((f) => {
                      // Round 59 · Item 2 — once the agent manually edits the due date, stop auto-syncing.
                      const shouldSync = !dueManuallySet;
                      return { ...f, end_date: nextEnd, next_due_date: shouldSync ? nextEnd : f.next_due_date };
                    });
                  }} required data-testid="policy-end-input" />
                  <p className="text-[10px] text-slate-500">Defaults to Start + 364 days. Edit freely for multi-year or short-term.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="next_due_date">Next Due Date *</Label>
                  <Input id="next_due_date" type="date" value={formData.next_due_date} onChange={(e) => { setDueManuallySet(true); setFormData({...formData, next_due_date: e.target.value}); }} required data-testid="policy-due-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger data-testid="policy-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Lapsed">Lapsed</SelectItem>
                      <SelectItem value="Lost">Lost — moves to Lost Leads</SelectItem>
                      <SelectItem value="Surrendered">Surrendered</SelectItem>
                      <SelectItem value="Matured">Matured</SelectItem>
                      <SelectItem value="Claimed">Claimed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="nominee">Nominee</Label>
                  <Input id="nominee" value={formData.nominee} onChange={(e) => setFormData({...formData, nominee: e.target.value})} data-testid="policy-nominee-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="policy_stage">Policy Stage</Label>
                  <Select value={formData.policy_stage || 'not_set'} onValueChange={(v) => setFormData({...formData, policy_stage: v === 'not_set' ? '' : v})}>
                    <SelectTrigger data-testid="policy-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_set">Not set</SelectItem>
                      <SelectItem value="New Business">New</SelectItem>
                      <SelectItem value="Renewal">Renewal</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500">Used by the commission tracker to pick the correct rate.</p>
                </div>
                <div className="col-span-2 rounded-md border border-blue-100 bg-blue-50/40 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Policy Document</p>
                      <p className="text-[11px] text-slate-500">Optional. Attach the PDF/scan (max 10MB). Uploads after save.{editingPolicy ? ' Existing attachments remain — this adds another.' : ''}</p>
                    </div>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => setPolicyDocFile(e.target.files?.[0] || null)}
                      className="text-xs" data-testid="policy-doc-file-input" />
                  </div>
                  {policyDocFile && (
                    <p className="text-xs text-blue-800">Selected: <strong>{policyDocFile.name}</strong> ({Math.round(policyDocFile.size / 1024)} KB)</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="policy-submit-button">{editingPolicy ? 'Update' : 'Create'} Policy</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle style={{ fontFamily: 'Manrope' }}>
            {selectedClient
              ? <>Policies for <span className="text-blue-800">{selectedClient.name}</span> ({filteredPolicies.length})</>
              : `All Policies (${filteredPolicies.length}${currentCategory !== 'all' ? ` in ${(CAT_LABEL[currentCategory]||{}).label || currentCategory}` : ''})`}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Round 59 · Item 1 — client search typeahead */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); if (selectedClient) setSelectedClient(null); }}
                placeholder="Search client by name, mobile or ID…"
                className="pl-8 pr-7 h-8 w-[280px] text-xs"
                data-testid="policies-client-search"
              />
              {clientSearch && (
                <button type="button" onClick={() => { setClientSearch(''); setSelectedClient(null); }} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded" data-testid="policies-client-search-clear">
                  <X className="h-3 w-3 text-slate-500" />
                </button>
              )}
              {clientSearch && !selectedClient && (
                <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="policies-client-search-results">
                  {(() => {
                    const q = clientSearch.trim().toLowerCase();
                    const matches = clients.filter(c => {
                      const h = `${c.name || ''} ${c.primary_mobile || ''} ${c.customer_code || ''} ${c.email || ''}`.toLowerCase();
                      return h.includes(q);
                    }).slice(0, 20);
                    if (matches.length === 0) return <p className="text-xs text-slate-500 py-3 text-center">No clients match.</p>;
                    return matches.map(c => {
                      const count = policies.filter(p => p.client_id === c.client_id).length;
                      return (
                        <button
                          key={c.client_id}
                          type="button"
                          onClick={() => { setSelectedClient(c); setClientSearch(c.name); }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-slate-50 text-left"
                          data-testid={`policies-client-search-option-${c.client_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{c.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">{[c.customer_code, c.primary_mobile].filter(Boolean).join(' · ') || '—'}</div>
                          </div>
                          <Badge variant="outline" className="text-[10px] flex-none"><FileText className="h-3 w-3 mr-0.5" />{count}</Badge>
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="policies-category-tabs">
            {['all', 'insurance:life', 'insurance:general', 'investment', 'loans'].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => { const p = new URLSearchParams(searchParams); if (k === 'all') p.delete('category'); else p.set('category', k); setSearchParams(p); }}
                data-testid={`policies-cat-${k.replace(/:/g, '-')}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${currentCategory === k ? 'bg-blue-800 text-white border-blue-800' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}
              >
                {k === 'all' ? 'All' : (CAT_LABEL[k]?.label || k)}
              </button>
            ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPolicies.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              {currentCategory === 'all'
                ? 'No policies yet. Add your first policy to get started!'
                : `No ${(CAT_LABEL[currentCategory]||{}).label || currentCategory} products yet.`}
            </p>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">S.No.</TableHead>
                  <TableHead><SortableTh sortKey="policy_number" state={policiesTable}>Policy Number</SortableTh></TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead><SortableTh sortKey="product_name" state={policiesTable}>Product</SortableTh></TableHead>
                  <TableHead><SortableTh sortKey="insurer" state={policiesTable}>Insurer</SortableTh></TableHead>
                  <TableHead><SortableTh sortKey="premium_amount" state={policiesTable}>Premium</SortableTh></TableHead>
                  <TableHead>Total (incl GST)</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <span>Stage</span>
                      <select
                        className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white"
                        value={policiesTable.filters.policy_stage || 'all'}
                        onChange={(e) => policiesTable.setFilter('policy_stage', e.target.value)}
                        data-testid="filter-policy-stage"
                      >
                        <option value="all">All</option>
                        <option value="New Business">New</option>
                        <option value="Renewal">Renewal</option>
                        <option value="Both">Both</option>
                        <option value="__not_set__">Not set</option>
                      </select>
                    </div>
                  </TableHead>
                  <TableHead><SortableTh sortKey="next_due_date" state={policiesTable}>Next Due</SortableTh></TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <span>Status</span>
                      <select className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white" value={policiesTable.filters.status_display || 'all'} onChange={(e) => policiesTable.setFilter('status_display', e.target.value)} data-testid="filter-policy-status">
                        <option value="all">All</option><option value="Active">Active</option><option value="Matured">Matured</option><option value="Lapsed">Lapsed</option><option value="Lost">Lost</option>
                      </select>
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policiesTable.pageRows.map((policy, idx) => {
                  const cat = categoryOf(policy.product_type);
                  const meta = CAT_LABEL[cat] || CAT_LABEL['insurance:other'];
                  return (
                  <TableRow key={policy.policy_id} data-testid={`policy-row-${policy.policy_id}`} className="cursor-pointer hover:bg-blue-50/50" onClick={(e) => {
                    // Ignore clicks that originated on action buttons (edit/delete).
                    if (e.target.closest('button')) return;
                    navigate(`/policies/${policy.policy_id}`);
                  }}>
                    <TableCell className="text-xs text-slate-500">{policiesTable.startIndex + idx + 1}</TableCell>
                    <TableCell className="font-medium">{policy.policy_number}</TableCell>
                    <TableCell><Badge className={meta.color}>{meta.label}</Badge></TableCell>
                    <TableCell>{getClientName(policy.client_id)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{policy.product_name}</p>
                        <p className="text-xs text-slate-500">{policy.product_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>{policy.insurer || '-'}</TableCell>
                    <TableCell>{formatCurrency(policy.premium_amount)}</TableCell>
                    <TableCell>{policy.total_premium ? formatCurrency(policy.total_premium) : '-'}</TableCell>
                    <TableCell>
                      {policy.policy_stage ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800" data-testid={`policy-stage-${policy.policy_id}`}>
                          {policy.policy_stage === 'New Business' ? 'New' : policy.policy_stage}
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`policy-stage-${policy.policy_id}`}>Not set</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(policy.next_due_date)}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); cycleStatus(policy); }}
                        data-testid={`quick-status-${policy.policy_id}`}
                        title={`Click to cycle → ${policy.status === 'Active' ? 'Lapsed' : policy.status === 'Lapsed' ? 'Lost' : 'Active'}`}
                        className="focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-full"
                      >
                        <Badge className={`${getStatusColor(policy.status)} cursor-pointer hover:opacity-80 select-none`}>{policy.status} ⟳</Badge>
                      </button>
                      {policy.renewal_status === 'lost' && (
                        <span
                          className="ml-1 inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white align-middle"
                          title={policy.loss_reason ? `Lost · ${policy.loss_reason}${policy.loss_notes ? ' — ' + policy.loss_notes : ''}` : 'Marked Lost'}
                          data-testid={`policy-lost-tag-${policy.policy_id}`}
                        >
                          LOST
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(policy)} data-testid={`edit-policy-${policy.policy_id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(policy.policy_id)} data-testid={`delete-policy-${policy.policy_id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <PaginationBar state={policiesTable} testId="policies-pagination" />
            </>
          )}
        </CardContent>
      </Card>

      {/* Round 44 · Loss-reason picker — reused for both edit-form save AND one-tap badge. */}
      <LossReasonDialog
        open={lostReasonState.open}
        title={lostReasonState.policy ? `Mark "${lostReasonState.policy.policy_number}" as Lost` : 'Mark as Lost'}
        description="Pick the closest reason so the Lost Leads Analysis report keeps clustering cleanly."
        confirming={lostReasonSaving}
        onCancel={() => { if (!lostReasonSaving) setLostReasonState({ open: false, source: '', policy: null }); }}
        onConfirm={submitPolicyLostReason}
      />

      {/* Round 59 · Item 4 — permanent delete with cascade confirmation. */}
      <AlertDialog open={!!deletingPolicy} onOpenChange={(o) => { if (!o) setDeletingPolicy(null); }}>
        <AlertDialogContent data-testid="delete-policy-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this policy permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-slate-900">{deletingPolicy?.policy_number}</strong> — {deletingPolicy?.product_name || ''} ({deletingPolicy?.insurer || ''}).<br />
              This will also remove any commission log, renewal reminders, in-app notifications and tasks tied to this policy. The client record itself is kept.
              <br /><br />
              This action can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-policy-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-policy-confirm">
              Delete policy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Policies;